import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { getValueByTarget } from '../../../utils/parse';
import {
    NumberOfVehiclesOptions,
    RoleOfVehiclesOptions,
    UsageTimeOptionsAnnual,
    UsageTimeOptionsMonthly,
    CarAmountOptions,
    InsuranceOptions,
} from '../../../utils/EstimateFormData';
import { useSelector } from 'react-redux';
import { transFormWon2 } from '../../../utils/numbers';
import { getEstimateCookieDataAndDecrypt, isChkAddressSeoul } from '../../../lib/Util';

import moment from 'moment';
import vutler_dojang from '../../../styles/img/vutler_dojang.png'
import html2canvas from 'html2canvas';
import { estimateDescriptionLine, EstimateDescriptionListItem } from './estimateDescriptionHelpers';

/** 견적서 시간당 기준 단가 (할인 전) */
const BASE_HOUR_PRICE = 30000;

/** 견적 테이블 가로선 (위·아래 굵게, 중간 일반) */
const ESTIMATE_TABLE_BORDER_THICK = '2px solid #111';
const ESTIMATE_TABLE_BORDER_NORMAL = '1px solid #111';

/** 견적 테이블 영역 공통 글자 크기 */
const ESTIMATE_TABLE_FONT = 14;

const tableFontBase = {
    fontSize: ESTIMATE_TABLE_FONT,
    lineHeight: 1.35,
};

const formatAmount = (amount) => transFormWon2(amount ?? 0);

const HourlyUnitPriceCell = ({ basePrice, discountedPrice, discountPercent }) => (
    <div
        style={{
            ...tableFontBase,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 4,
            width: '100%',
            fontVariantNumeric: 'tabular-nums',
        }}
    >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', gap: 4 }}>
            <span style={{ color: '#999', textDecoration: 'line-through' }}>
                {formatAmount(basePrice)}
            </span>
            <span style={{ color: '#333' }}>→</span>
            <span style={{ color: '#111' }}>
                {formatAmount(discountedPrice)}
            </span>
        </div>
        {discountPercent > 0 && (
            <span style={{ color: '#e85d04' }}>
                {discountPercent}% off
            </span>
        )}
    </div>
);

const EstimateAmountRow = ({ label, amount }) => (
    <tr style={{ borderBottom: ESTIMATE_TABLE_BORDER_NORMAL }}>
        <td colSpan={5} style={{ ...estimateCellStyle.label, textAlign: 'left' }}>[OPT] {label}</td>
        <td style={estimateCellStyle.amount}>{formatAmount(amount)}</td>
    </tr>
);

const estimateCellStyle = {
    label: {
        ...tableFontBase,
        padding: '14px 8px',
        fontWeight: 500,
        color: '#111',
        verticalAlign: 'middle',
    },
    header: {
        ...tableFontBase,
        padding: '10px 6px',
        fontWeight: 600,
        color: '#666',
        textAlign: 'center',
        borderBottom: ESTIMATE_TABLE_BORDER_NORMAL,
        verticalAlign: 'bottom',
    },
    data: {
        ...tableFontBase,
        padding: '14px 6px',
        textAlign: 'center',
        verticalAlign: 'middle',
        color: '#333',
    },
    amount: {
        ...tableFontBase,
        padding: '14px 8px',
        fontVariantNumeric: 'tabular-nums',
        fontWeight: 600,
        textAlign: 'right',
        verticalAlign: 'middle',
    },
    totalLabel: {
        ...tableFontBase,
        padding: '16px 8px',
        fontWeight: 700,
        letterSpacing: '0.02em',
        verticalAlign: 'middle',
    },
    empty: {
        padding: '14px 6px',
    },
};

const buildUsageTimeMeta = (subscription, usageTimeValue) => {
    const options = subscription === 'annual' ? UsageTimeOptionsAnnual : UsageTimeOptionsMonthly;
    const selected = options.find((item) => item.value === usageTimeValue);
    if (!selected) {
        return {
            minHourPerDay: '-',
            basicHours: 0,
            hourPrice: 0,
        };
    }
    const basicHours = (selected.estimateDay ?? 0) * (selected.estimateHour ?? 0);
    return {
        minHourPerDay: selected.estimateMinHour ?? '-',
        basicHours,
        hourPrice: selected.hourPrice ?? 0,
    };
};

const buildSupplementaryRows = (BookingOption, contractMonthOptions) => {
    const rows = [];
    const { subscription, usageTime, vehicleOption, contractTerms1 } = BookingOption;

    if (subscription === 'monthly') {
        const periodDiscount = getValueByTarget(
            contractMonthOptions,
            contractTerms1?.periodOfContract,
            'discount',
        );
        if (periodDiscount > 0) {
            rows.push({
                label: `계약 기간: ${getValueByTarget(contractMonthOptions, contractTerms1.periodOfContract, 'title')}`,
                amount: -periodDiscount,
            });
        }
    }

    const vehicleCountPrice = getValueByTarget(
        NumberOfVehiclesOptions,
        vehicleOption?.numberOfVehicles,
        'price',
    );
    if (vehicleCountPrice > 0) {
        rows.push({
            label: `등록 차량 대수: ${getValueByTarget(NumberOfVehiclesOptions, vehicleOption.numberOfVehicles, 'title')}`,
            amount: vehicleCountPrice,
        });
    }

    const carAmountPrice = getValueByTarget(CarAmountOptions, vehicleOption?.carAmount, 'price');
    if (carAmountPrice > 0) {
        rows.push({
            label: `차량 가격: ${getValueByTarget(CarAmountOptions, vehicleOption.carAmount, 'title')}`,
            amount: carAmountPrice,
        });
    }

    const insuranceDiscount = getValueByTarget(InsuranceOptions, vehicleOption?.insurance, 'discount');
    if (insuranceDiscount > 0) {
        rows.push({
            label: `보험: ${getValueByTarget(InsuranceOptions, vehicleOption.insurance, 'title')}`,
            amount: -insuranceDiscount,
        });
    }

    const rolePrice = getValueByTarget(
        RoleOfVehiclesOptions(subscription, usageTime),
        vehicleOption?.roleOfVehicles,
        'price',
    );
    if (rolePrice > 0) {
        rows.push({
            label: `서비스: ${getValueByTarget(RoleOfVehiclesOptions(subscription, usageTime), vehicleOption.roleOfVehicles, 'title')}`,
            amount: rolePrice,
        });
    }

    return rows;
};

/**
 * 견적서 캡처 → localStorage (data URL) 페이지
 * html2canvas 대상: #estimate_form2
 */
const EstimateDownloadDataUrl = () => {
    const { BookingOption, ContractMonthArray } = useSelector((state) => state);
    const [loading, setLoading] = useState(false);
    const [firstProcess, setFirstProcess] = useState(false);
    const [UserName, SetUserName] = useState('');
    const [ConfirmEstimateDescription2, SetConfirmEstimateDescription2] = useState([]);

    const estimateTable = useMemo(() => {
        const { subscription, usageTime, contractTerms1, totalAmount } = BookingOption;
        const usageMeta = buildUsageTimeMeta(subscription, usageTime);
        const discountPercent =
            usageMeta.hourPrice > 0
                ? Math.round((1 - usageMeta.hourPrice / BASE_HOUR_PRICE) * 100)
                : 0;
        const basicLineAmount = usageMeta.basicHours * usageMeta.hourPrice;

        const usageUnit =
            subscription === 'monthly'
                ? getValueByTarget(ContractMonthArray.select, contractTerms1?.periodOfContract, 'title') ?? '1개월'
                : '1년';

        const serviceLabel =
            subscription === 'annual'
                ? '연간 모시러 서비스 기본 이용료'
                : '월간 모시러 서비스 기본 이용료';

        const supplementaryRows = buildSupplementaryRows(BookingOption, ContractMonthArray.select);

        return {
            serviceLabel,
            usageUnit,
            minHourPerDay: usageMeta.minHourPerDay,
            basicHours: usageMeta.basicHours,
            baseHourPrice: BASE_HOUR_PRICE,
            hourPrice: usageMeta.hourPrice,
            discountPercent,
            basicLineAmount,
            supplementaryRows,
            estimatedTotal: totalAmount ?? 0,
        };
    }, [BookingOption, ContractMonthArray.select]);

    const sleep = (time) => {
        return new Promise((resolve) => {
            setTimeout(() => {
                resolve();
            }, time);
        });
    };

    const print = async () => {
        const canvas = await html2canvas(document.querySelector('#estimate_form2'), {
            width: 635,
            height: 1500,
        });
        return canvas.toDataURL('image/png');
    };

    useEffect(() => {
        const info = getEstimateCookieDataAndDecrypt();
        SetUserName(info.UserName ?? '');

        const seoulResult = isChkAddressSeoul(
            BookingOption?.contractTerms1?.serviceArea?.departure?.address1,
            BookingOption?.contractTerms1?.serviceArea?.destination?.address1,
        );
        const addedArray = [];
        if (!seoulResult) {
            addedArray.push('예상 출발지/종료지가 서울 이외일 경우, 지역할증이 부과될 수 있습니다.');
        }
        addedArray.push(`본 견적서의 내용은 ${moment().add(1, 'months').format('yyyy년 MM월 DD일')}까지 유효합니다.`);

        if (BookingOption.subscription === 'annual') {
            SetConfirmEstimateDescription2([
                '본 견적 내 항목은 모두 VAT 포함입니다.',
                '실 탑승자 5인 까지를 기준으로 한 견적이며, 6인 이상 이용이 필요하신 경우 컨시어지로 문의 바랍니다.',
                estimateDescriptionLine('구입 시간 모두 소진 시 추가 구입이 가능합니다.', '#e53935'),
                '본 견적서 내 포함되지 않은 항목이라도 실제 서비스 이용에 따라 할증 요금이 사전 안내 후 청구될 수 있습니다.',
                ...addedArray,
            ]);
        } else {
            SetConfirmEstimateDescription2([
                '본 견적 내 항목은 모두 VAT 포함입니다.',
                '실 탑승자 5인 까지를 기준으로 한 견적이며, 6인 이상 이용이 필요하신 경우 컨시어지로 문의 바랍니다.',
                '기본 이용시간 외 추가 이용도 가능하며, 시간당 단가가 반영되어 추가 청구됩니다.',
                '본 견적서 내 포함되지 않은 항목이라도 실제 서비스 이용에 따라 할증 요금이 사전 안내 후 청구될 수 있습니다.',
                ...addedArray,
            ]);
        }

        setLoading(true);

        const ProcessFunc = async () => {
            if (!firstProcess) {
                await sleep(2000);
                const imgdata = await print();
                localStorage.setItem('estimate_img', imgdata.replace('data:image/png;base64,', ''));
                localStorage.setItem('estimate_img_processed', false);
            }
            setFirstProcess(true);
        };
        ProcessFunc();
    }, [BookingOption]);

    useEffect(() => {
        // console.log('helloworld');
    }, [loading]);

    return (
        <div
            id="estimate_form2"
            style={{
                padding: '30px 20px',
                width: '635px',
                minHeight: '891px',
                backgroundColor: '#fff',
                boxSizing: 'border-box',
            }}
        >
            <div className={classNames('booking-wrap2')}>
                <div style={{ textAlign: 'center', fontWeight: 'bold', fontSize: '40px' }}>
                    {BookingOption.subscription === 'annual' ? '연간모시러 견적서' : '월간모시러 견적서'}
                </div>

                <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 24 }}>
                    <div style={{ fontSize: '18px' }}>{moment().format('yyyy년 MM월 DD일')}</div>
                    <div style={{ textAlign: 'right' }}>
                        <span style={{ display: 'block', padding: '5px 0', fontSize: '18px' }}>{UserName} 고객님</span>
                        <span style={{ display: 'block', padding: '5px 0', fontSize: '18px' }}>아래와 같이 견적서를 발송합니다.</span>
                    </div>
                </div>

                <div className={classNames('confirm-booking-wrap2')} style={{ marginTop: 28 }}>
                    <div className={classNames('confirm-booking-content2')}>
                        <table style={{
                            width: '100%',
                            borderCollapse: 'collapse',
                            tableLayout: 'fixed',
                            borderTop: ESTIMATE_TABLE_BORDER_THICK,
                            borderBottom: ESTIMATE_TABLE_BORDER_THICK,
                            ...tableFontBase,
                        }}>
                            <colgroup>
                                <col style={{ width: '22%' }} />
                                <col style={{ width: '11%' }} />
                                <col style={{ width: '15%' }} />
                                <col style={{ width: '12%' }} />
                                <col style={{ width: '25%' }} />
                                <col style={{ width: '15%' }} />
                            </colgroup>
                            <thead>
                                <tr>
                                    <th style={{ ...estimateCellStyle.header, textAlign: 'left' }} />
                                    <th style={estimateCellStyle.header}>이용단위</th>
                                    <th style={estimateCellStyle.header}>1일(건)<br />최소이용시간</th>
                                    <th style={estimateCellStyle.header}>기본<br />이용시간</th>
                                    <th style={estimateCellStyle.header}>단가<br />(per hour)</th>
                                    <th style={estimateCellStyle.header}>금액</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr style={{ borderBottom: ESTIMATE_TABLE_BORDER_NORMAL }}>
                                    <td style={estimateCellStyle.label}>{estimateTable.serviceLabel}</td>
                                    <td style={estimateCellStyle.data}>{estimateTable.usageUnit}</td>
                                    <td style={estimateCellStyle.data}>{estimateTable.minHourPerDay}</td>
                                    <td style={estimateCellStyle.data}>{estimateTable.basicHours}</td>
                                    <td style={{ ...estimateCellStyle.data, padding: '12px 4px' }}>
                                        <HourlyUnitPriceCell
                                            basePrice={estimateTable.baseHourPrice}
                                            discountedPrice={estimateTable.hourPrice}
                                            discountPercent={estimateTable.discountPercent}
                                        />
                                    </td>
                                    <td style={estimateCellStyle.amount}>
                                        {formatAmount(estimateTable.basicLineAmount)}
                                    </td>
                                </tr>
                                {estimateTable.supplementaryRows.map((row, index) => (
                                    <EstimateAmountRow key={index} label={row.label} amount={row.amount} />
                                ))}
                            </tbody>
                            <tfoot>
                                <tr style={{ borderTop: ESTIMATE_TABLE_BORDER_NORMAL }}>
                                    <td colSpan={5} style={estimateCellStyle.totalLabel}>
                                        ESTIMATED TOTAL
                                    </td>
                                    <td style={{
                                        ...estimateCellStyle.amount,
                                        fontWeight: 700,
                                        padding: '16px 8px',
                                    }}>
                                        {formatAmount(estimateTable.estimatedTotal)}
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>

                    <div className={classNames('box-light-grey-1 footer-contents2')} style={{ marginTop: 24 }}>
                        <ul className={classNames('list-style')}>
                            {ConfirmEstimateDescription2.map((item, index) => (
                                <li key={index}>
                                    <EstimateDescriptionListItem item={item} />
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-around', alignItems: 'center', marginBottom: '100px', marginTop: 32 }}>
                        <div style={{ fontSize: '18px' }}>
                            <span style={{ display: 'block', padding: '5px 0', fontSize: '18px', fontWeight: 'bold' }}>주식회사 버틀러</span>
                            <span style={{ display: 'block', padding: '5px 0', fontSize: '18px' }}>서울 강남구 테헤란로 151, 4층</span>
                        </div>
                        <div style={{ fontSize: '18px', textAlign: 'right' }}>
                            <span style={{ display: 'block', padding: '5px 0', fontSize: '18px' }}>463-87-00162</span>
                            <span style={{ display: 'block', padding: '5px 0', fontSize: '18px' }}>이근우</span>
                        </div>
                        <div style={{ textAlign: 'left' }}>
                            <span style={{ display: 'block', fontSize: '18px' }}><img src={vutler_dojang} style={{ width: '80%' }} alt='' /></span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EstimateDownloadDataUrl;
